import styled from "styled-components";
import { useTranslation } from "react-i18next";

const EMPTY_VALUE = "---";

const ResubmissionDetailsBox = styled.div`
  margin-top: 8px;
  padding: 8px 12px;
  line-height: 1.45;
  border-radius: 6px;
  background: #f8f9fa;
  border: 1px solid var(--th-border-color);
`;
const ChangeList = styled.ul`
  margin: 0;
  padding-left: 18px;
`;
const ChangeListItem = styled.li`
  white-space: pre-wrap;
  word-break: break-word;
  & + & {
    margin-top: 6px;
  }
`;
const ChangeLabel = styled.span`
  font-weight: 500;
`;
const ChangeSectionTitle = styled.li`
  font-weight: 500;
  list-style: none;
  margin-bottom: 6px;
`;
const ChangeDivider = styled.li`
  margin: 10px 0;
  list-style: none;
  border-top: 1px solid var(--th-border-color);
`;

const isPlainObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value);

const hasValue = (value) =>
  value !== null && value !== undefined && value !== "";

const getTextValue = (value) => (hasValue(value) ? String(value) : EMPTY_VALUE);

const formatValueChange = ({ previousValue, currentValue }) =>
  `${previousValue} → ${currentValue}`;

const getAvailabilityChange = (details, t) => {
  if (!hasValue(details.optimalAvailability)) {
    return null;
  }
  return {
    label: t("leadActivities.resubmissionDetails.fields.optimalAvailability"),
    change: formatValueChange({
      previousValue: getTextValue(details.previousOptimalAvailability),
      currentValue: getTextValue(details.optimalAvailability)
    })
  };
};

const getChangedAnswerItems = (changedAnswers) => {
  if (!Array.isArray(changedAnswers)) return [];
  return changedAnswers.map((changedAnswer) => ({
    label: getTextValue(changedAnswer.question),
    change: formatValueChange({
      previousValue: getTextValue(changedAnswer.previousAnswer),
      currentValue: getTextValue(changedAnswer.answer)
    })
  }));
};

const ResubmissionDetailsDisplay = ({ details }) => {
  const { t } = useTranslation();

  if (!isPlainObject(details)) {
    return null;
  }

  const availabilityChange = getAvailabilityChange(details, t);
  const changedAnswerItems = getChangedAnswerItems(details.changedAnswers);

  if (!availabilityChange && !changedAnswerItems.length) {
    return null;
  }

  return (
    <ResubmissionDetailsBox>
      <div className="fw-bold mb-1">
        {t("leadActivities.resubmissionDetails.title")}
      </div>
      <ChangeList>
        {availabilityChange && (
          <ChangeListItem>
            <ChangeLabel>{availabilityChange.label}</ChangeLabel>:{" "}
            {availabilityChange.change}
          </ChangeListItem>
        )}
        {availabilityChange && changedAnswerItems.length > 0 && (
          <ChangeDivider />
        )}
        {changedAnswerItems.length > 0 && (
          <ChangeSectionTitle>
            {t("leadActivities.resubmissionDetails.fields.changedAnswers")}:
          </ChangeSectionTitle>
        )}
        {changedAnswerItems.map(({ label, change }, index) => (
          <ChangeListItem key={`${label}-${index}`}>
            <ChangeLabel>{label}</ChangeLabel>: {change}
          </ChangeListItem>
        ))}
      </ChangeList>
    </ResubmissionDetailsBox>
  );
};

export default ResubmissionDetailsDisplay;
